import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { Result } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { TelegramId } from '@root/entities/auth/model/telegram-id'
import { CurrencyAddress } from '@root/entities/currency'
import { PremiumId } from '@root/entities/premium'
import { DelugeId } from '@root/entities/user-short'
import { WalletAddress } from '@root/entities/wallet'
import { useTranslation } from '@root/shared/localization'
import { Input } from '@root/shared/ui-kit'

import { reatomBackOfficePremiumChangeForm } from '../atoms/reatom-back-office-premium-change-form'

type BackOfficePremiumChangeFormValues = {
  readonly premiumId: string
  readonly tokenAddress: string
  readonly newDelugeId: string
  readonly newOwnerWalletAddress: string
  readonly newTgId: string
}

export const BackOfficePremiumChangeForm: React.FC = () => {
  const { t } = useTranslation()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomBackOfficePremiumChangeForm(), [])
  const [requestState] = useAtom(model.requestState)
  const executeRequest = useAction(model.requestState.execute)

  const form = useForm<BackOfficePremiumChangeFormValues>({
    mode: 'all',
    defaultValues: {
      premiumId: '',
      tokenAddress: '',
      newDelugeId: '',
      newOwnerWalletAddress: '',
      newTgId: '',
    },
  })

  const handleSubmit = form.handleSubmit(
    async ({
      premiumId,
      tokenAddress,
      newDelugeId,
      newOwnerWalletAddress,
      newTgId,
    }) => {
      if (!premiumId && !tokenAddress) {
        form.setError('premiumId', {
          type: 'manual',
          message: t('back_office.premium.id_or_token_required'),
        })
        form.setError('tokenAddress', {
          type: 'manual',
          message: t('back_office.premium.id_or_token_required'),
        })
        return
      }

      const result = await executeRequest({
        premiumId: premiumId ? PremiumId(premiumId) : undefined,
        tokenAddress: tokenAddress ? CurrencyAddress(tokenAddress) : undefined,
        newDelugeId: DelugeId(newDelugeId),
        newOwnerWalletAddress: WalletAddress(newOwnerWalletAddress),
        newTgId: newTgId ? TelegramId(newTgId) : undefined,
      })

      if (Result.isFailure(result)) {
        return
      }

      form.reset()
    },
  )

  return (
    <Root>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit}>
          <Controller
            name="premiumId"
            control={form.control}
            disabled={requestState.loading}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={t('back_office.premium.premium_id')}
                size="m"
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="tokenAddress"
            control={form.control}
            disabled={requestState.loading}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={t('back_office.premium.token_address')}
                size="m"
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="newDelugeId"
            control={form.control}
            disabled={requestState.loading}
            rules={{ required: t('validation.required') }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                required
                label={t('back_office.premium.new_deluge_id')}
                size="m"
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="newOwnerWalletAddress"
            control={form.control}
            disabled={requestState.loading}
            rules={{ required: t('validation.required') }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                required
                label={t('back_office.premium.new_owner_wallet')}
                size="m"
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="newTgId"
            disabled={requestState.loading}
            control={form.control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={t('back_office.premium.new_tg_id')}
                size="m"
                error={fieldState.error?.message}
              />
            )}
          />

          <Button
            type="submit"
            variant="solid"
            color="primary"
            radius="rounded"
            size="m"
            disabled={!form.formState.isValid || requestState.loading}
            busy={requestState.loading}
          >
            {t('back_office.premium.submit')}
          </Button>
        </Form>
      </FormProvider>
    </Root>
  )
}

const Root = styled.div`
  padding: 12px;
  max-width: 480px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`
